import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button/new';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { DropdownItem } from '@atlaskit/dropdown-menu';
import EditorAddIcon from '@atlaskit/icon/core/add';
import WorkItemIcon from '@atlaskit/icon/core/work-item';
import WorkItemsIcon from '@atlaskit/icon/core/work-items';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import { editorExperiment } from '@atlaskit/tmp-editor-statsig/experiments';
import {
	ToolbarDropdownItem,
	PlusIcon,
	WorkItemIcon as WorkItemIconAIFC,
	WorkItemsIcon as WorkItemsIconAIFC,
} from '@atlaskit/editor-toolbar';

import { useAIEventsInstrumentation } from '@atlassian/ai-analytics';
import type { ContentType } from '@atlassian/ai-issue-create';

import { useRightSidebarContext } from '@confluence/page-layout-context';
import {
	useSetIsCreatedWithAi,
	useSetIsCreatedFromTable,
	useSetIsCreatedFromActionItems,
} from '@confluence/issue-create-side-panel/entry-points/BulkCreateContextProvider';
import {
	useIssueCreateSidePanelContext,
	PageMode,
} from '@confluence/issue-create-side-panel/entry-points/IssueCreateSidePanelContextProvider';
import { useIsAIIssueCreationEnabled } from '@confluence/issue-create-side-panel/entry-points/useIsAIIssueCreationEnabled';
import { findActionItemElement } from '@confluence/issue-create-side-panel/entry-points/actionItemUtils';
import { useIsSidePanelOpen } from '@confluence/side-panel-state-container';
import { fg } from '@confluence/feature-gating';
import { usePageInfo } from '@confluence/page-info/entry-points/usePageInfo';
import { StatsigConfigurations } from '@confluence/statsig-client/entry-points/StatsigConfigurations';
import { expValEquals } from '@confluence/feature-experiments';

import { createMultipleJiraIssuesHandle } from './builtInButtonHandlers';
import {
	createMultipleJiraIssuesHandleModernized,
	createSingleAiJiraIssueHandle,
	createSingleJiraIssueHandle,
} from './createJiraIssueHandlers';
import type { CreateIssueRecordedProps } from './HighlightActions';

type CreateJiraIssueActionsProps = {
	activeSelection?: Selection | null;
	completeKey: string;
	container: any;
	contentId: string;
	contentType?: ContentType;
	lastModified?: string | null;
	showCreateMultipleIssuesButton: boolean;
	setRecordedClick: React.Dispatch<React.SetStateAction<CreateIssueRecordedProps | undefined>>;
	setIsWarningDialogOpen: React.Dispatch<React.SetStateAction<Boolean>>;
};

export const IssueCreationType = {
	SINGLE_ISSUE: 'SINGLE_ISSUE',
	MULTIPLE_ISSUE: 'MULTIPLE_ISSUE',
	AI_ISSUE: 'AI_ISSUE',
} as const;

type IssueCreationType = (typeof IssueCreationType)[keyof typeof IssueCreationType];

// Helper function to find the parent <table> element of a given node
const findParentTable = (node: Node): HTMLTableElement | null => {
	let currentNode: Node | null = node;
	while (currentNode) {
		if (
			currentNode.nodeType === Node.ELEMENT_NODE &&
			(currentNode as HTMLElement).tagName === 'TABLE'
		) {
			return currentNode as HTMLTableElement;
		}
		currentNode = currentNode.parentNode;
	}
	return null;
};

// Use this helper function when we have the modernized bulk create ready.
const isMultiRowTableSelection = (selection?: Selection | null): boolean => {
	if (!selection || selection.rangeCount === 0) {
		return false;
	}
	const range = selection.getRangeAt(0);
	const startContainer = range.startContainer;
	const endContainer = range.endContainer;
	// Helper function to find the parent <tr> element of a given node
	const findParentTableRow = (node: Node): HTMLTableRowElement | null => {
		while (node) {
			if ((node as HTMLElement).tagName === 'TR') {
				return node as HTMLTableRowElement;
			}
			node = node.parentNode as Node;
		}
		return null;
	};
	// Find the closest <tr> ancestor for both start and end of the selection
	const startRow = findParentTableRow(startContainer);
	const endRow = findParentTableRow(endContainer);
	if (!startRow || !endRow) {
		return false;
	}
	// If the start and end are not in the same row, it's a multi-row selection
	return startRow !== endRow;
};

/**
 * isTableSelection: returns true if the user highlights over only
 * table content.
 * @param selection
 * @returns {boolean}
 */
const isTableSelection = (selection?: Selection | null): boolean => {
	if (!selection || selection.rangeCount === 0) {
		return false;
	}
	const range = selection.getRangeAt(0);
	const startContainer = range.startContainer;
	const endContainer = range.endContainer;
	// Check if both the start and end container are within a table
	const startInTable = findParentTable(startContainer) !== null;
	const endInTable = findParentTable(endContainer) !== null;
	return startInTable && endInTable;
};

/**
 * numberOfActionItemsSelected: returns the number of action items selected
 * @param selection
 * @returns {number}
 */
export const numberOfActionItemsSelected = (selection?: Selection | null): number => {
	if (!selection || selection.rangeCount === 0) {
		return 0;
	}

	const range = selection.getRangeAt(0);

	if (range.collapsed) {
		return 0;
	}

	const startContainer = range.startContainer;
	const endContainer = range.endContainer;

	const startInActionContainer = findActionItemElement(startContainer);
	const endInActionContainer = findActionItemElement(endContainer);

	let commonAncestor;

	if (startInActionContainer || endInActionContainer) {
		const actionItemRange = range.cloneRange();
		actionItemRange.setStart(startInActionContainer || startContainer, 0);
		actionItemRange.setEnd(endInActionContainer || endContainer, 0);
		commonAncestor = actionItemRange.commonAncestorContainer;
	} else {
		// the selection might span across different containers
		// or be in between the start and end highlight
		commonAncestor = range.commonAncestorContainer;
	}

	const walker = document.createTreeWalker(commonAncestor, NodeFilter.SHOW_ELEMENT, null);

	let node: Node | null = walker.currentNode;
	let actionItemsInSelection = 0;
	while (node) {
		if (range.intersectsNode(node)) {
			const element = node as HTMLElement;
			if (element.dataset?.taskLocalId !== undefined) {
				actionItemsInSelection++;
			}
		}
		node = walker.nextNode();
	}
	return actionItemsInSelection;
};

const getIssueCreationType = ({
	isAIIssueCreationEnabled,
}: {
	isAIIssueCreationEnabled: boolean;
}) => {
	if (isAIIssueCreationEnabled) {
		return IssueCreationType.AI_ISSUE;
	} else {
		return IssueCreationType.SINGLE_ISSUE;
	}
};

const userHasEditPermission = (pageInfo) => {
	if (!pageInfo) {
		return false;
	}

	const contentType = pageInfo.type;
	return (
		pageInfo.operations?.some(
			(op) => op?.operation === 'update' && op?.targetType === contentType,
		) || false
	);
};

const ConsolidatedCreateJiraIssueButton = ({
	completeKey,
	handleCreateIssueItemClicked,
	isAIIssueCreationEnabled,
	isSelectionMultipleRows,
}: {
	completeKey: string;
	handleCreateIssueItemClicked: (issueCreationType: IssueCreationType) => () => void;
	isAIIssueCreationEnabled: boolean;
	isSelectionMultipleRows: boolean;
}) => {
	const issueCreationType = isSelectionMultipleRows
		? IssueCreationType.MULTIPLE_ISSUE
		: getIssueCreationType({
				isAIIssueCreationEnabled,
			});

	const getIcon = (iconProps: any) => {
		if (issueCreationType === IssueCreationType.MULTIPLE_ISSUE) {
			return <WorkItemsIcon {...iconProps} label="" />;
		}
		return <WorkItemIcon {...iconProps} label="" />;
	};

	const getIconAIFC = (iconProps: any) => {
		if (issueCreationType === IssueCreationType.MULTIPLE_ISSUE) {
			return <WorkItemsIconAIFC {...iconProps} size="small" label="" />;
		}
		return <WorkItemIconAIFC {...iconProps} size="small" label="" />;
	};

	const getButtonText = () => {
		if (issueCreationType === IssueCreationType.MULTIPLE_ISSUE) {
			return (
				<FormattedMessage
					id="highlight-actions.create-multiple-jira-work-items"
					defaultMessage="Create Jira work items"
					description="Create multiple Jira work items"
				/>
			);
		}
		return (
			<FormattedMessage
				id="highlight-actions.create-jira-work-item"
				defaultMessage="Create Jira work item"
				description="Create a new Jira issue"
			/>
		);
	};

	if (
		fg('aifc_create_enabled') ||
		expValEquals('platform_editor_toolbar_aifc_confluence', 'isEnabled', true)
	) {
		return (
			<ToolbarDropdownItem
				testId="consolidated-create-jira-issue-button"
				data-key={completeKey}
				shouldTitleWrap={false}
				elemBefore={
					fg('platform_editor_toolbar_aifc_patch_8') ? (
						getIconAIFC({})
					) : (
						<PlusIcon size="small" label="" />
					)
				}
				onClick={handleCreateIssueItemClicked(issueCreationType)}
			>
				{getButtonText()}
			</ToolbarDropdownItem>
		);
	}

	return editorExperiment('platform_editor_controls', 'variant1') ? (
		<DropdownItem
			testId="consolidated-create-jira-issue-button"
			data-key={completeKey}
			shouldTitleWrap={false}
			elemBefore={getIcon({})}
			onClick={handleCreateIssueItemClicked(issueCreationType)}
		>
			{getButtonText()}
		</DropdownItem>
	) : (
		<Button
			id="create-jira-issue-button"
			appearance="subtle"
			iconBefore={(iconProps) => <EditorAddIcon {...iconProps} label="" />}
			data-key={completeKey}
			testId="consolidated-create-jira-issue-button"
			onClick={handleCreateIssueItemClicked(
				isSelectionMultipleRows
					? 'MULTIPLE_ISSUE'
					: getIssueCreationType({
							isAIIssueCreationEnabled,
						}),
			)}
			spacing="compact"
		>
			{fg('confluence-issue-terminology-refresh') ? (
				<FormattedMessage
					id="highlight-actions.create-jira-issue-issue-term-refresh"
					defaultMessage="Jira work item"
					description="Create a new Jira issue"
				/>
			) : (
				<FormattedMessage
					id="highlight-actions.create-jira-issue"
					defaultMessage="Jira issue"
					description="Create a new Jira issue"
				/>
			)}
		</Button>
	);
};

export const CreateJiraIssueActions = ({
	activeSelection,
	completeKey,
	container,
	contentId,
	contentType,
	lastModified,
	setRecordedClick,
	setIsWarningDialogOpen,
}: CreateJiraIssueActionsProps) => {
	const { trackAIInteractionInit } = useAIEventsInstrumentation();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const rightSidebarContext = useRightSidebarContext();
	const pressTracing = usePressTracing('highlight-actions.ai-issue-create.dropdown-item-click');
	// eslint-disable-next-line confluence-sweet-state/sweet-state-prefer-actions-hook
	const [, actions] = useIssueCreateSidePanelContext();
	const setIsCreatedWithAi = useSetIsCreatedWithAi();
	const setIsCreatedFromTable = useSetIsCreatedFromTable();
	const setIsCreatedFromActionItems = useSetIsCreatedFromActionItems();
	const isSidePanelOpen = useIsSidePanelOpen();
	const isAIIssueCreationEnabled = useIsAIIssueCreationEnabled();
	const isSelectionInTable = isTableSelection(activeSelection);
	// table creation has higher priority than action item creation
	const actionItemsInSelection = numberOfActionItemsSelected(activeSelection);
	const isActionItemInSelection =
		actionItemsInSelection > 0 &&
		!isSelectionInTable &&
		fg('confluence-create-work-items-from-action-items');
	const isMultiActionItemsInSelection =
		actionItemsInSelection > 1 &&
		!isSelectionInTable &&
		fg('confluence-create-work-items-from-action-items');
	const isSelectionMultipleRows =
		isMultiRowTableSelection(activeSelection) || isMultiActionItemsInSelection;
	const { pageInfo } = usePageInfo({ fetchPolicy: 'cache-first' });
	const hasEditPermission = useMemo(() => userHasEditPermission(pageInfo), [pageInfo]);

	const fireCreateIssueButtonClickedAnalytics = useCallback(
		(
			issueCreationType: IssueCreationType,
			isCreatedFromTable: boolean,
			isCreatedFromActionItems: boolean,
		) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: 'confluenceHighlightActionsMenu',
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'jiraIssueLink',
					type: 'ui',
					attributes: {
						issueCreationType,
						isDropdownItem: false,
						isCreatedFromTable,
						isCreatedFromActionItems,
						hasEditPermission,
						pageMode: PageMode.VIEW,
					},
				},
			}).fire();
		},
		[createAnalyticsEvent, hasEditPermission],
	);

	const handleCreateIssueItemClicked = useCallback(
		(issueCreationType: IssueCreationType) => () => {
			if (!isSidePanelOpen) {
				switch (issueCreationType) {
					case IssueCreationType.SINGLE_ISSUE:
						fireCreateIssueButtonClickedAnalytics(
							IssueCreationType.SINGLE_ISSUE,
							isSelectionInTable,
							isActionItemInSelection,
						);
						createSingleJiraIssueHandle({
							rightSidebarContext,
							actions,
							activeSelection,
							contentId,
							contentType,
							container,
							lastModified,
							hasEditPermission,
							isActionItemInSelection,
							pageMode: PageMode.VIEW,
						});
						break;
					case IssueCreationType.MULTIPLE_ISSUE:
						fireCreateIssueButtonClickedAnalytics(
							IssueCreationType.MULTIPLE_ISSUE,
							isSelectionInTable,
							isActionItemInSelection,
						);
						!StatsigConfigurations.getBoolean('confluence_legacy_bulk_jira_work_create', false)
							? createMultipleJiraIssuesHandleModernized({
									rightSidebarContext,
									pressTracing,
									activeSelection,
									actions,
									setIsCreatedWithAi,
									setIsCreatedFromTable,
									setIsCreatedFromActionItems,
									contentId,
									container,
									lastModified,
									hasEditPermission,
									pageMode: PageMode.VIEW,
								})
							: createMultipleJiraIssuesHandle(container, contentId, lastModified);
						break;
					case IssueCreationType.AI_ISSUE:
						fireCreateIssueButtonClickedAnalytics(
							IssueCreationType.AI_ISSUE,
							isSelectionInTable,
							isActionItemInSelection,
						);
						createSingleAiJiraIssueHandle({
							rightSidebarContext,
							actions,
							activeSelection,
							contentId,
							contentType,
							container,
							fireAiAnalytics: trackAIInteractionInit,
							lastModified,
							hasEditPermission,
							pageMode: PageMode.VIEW,
							isActionItemInSelection,
						});
						pressTracing();
						break;
				}
			} else {
				// ai specific data
				const aiData = {
					contentType,
					fireAiAnalytics: trackAIInteractionInit,
				};
				// record temp data
				const tempData = {
					rightSidebarContext,
					actions,
					activeSelection,
					contentId,
					container,
					lastModified,
					hasEditPermission,
					...(issueCreationType === IssueCreationType.AI_ISSUE ? aiData : {}),
				};

				setRecordedClick(tempData);
				setIsWarningDialogOpen(true);
			}
		},
		[
			isSidePanelOpen,
			fireCreateIssueButtonClickedAnalytics,
			isSelectionInTable,
			rightSidebarContext,
			actions,
			activeSelection,
			contentId,
			contentType,
			container,
			lastModified,
			pressTracing,
			setIsCreatedWithAi,
			setIsCreatedFromTable,
			setIsCreatedFromActionItems,
			trackAIInteractionInit,
			setRecordedClick,
			setIsWarningDialogOpen,
			hasEditPermission,
			isActionItemInSelection,
		],
	);

	return (
		<ConsolidatedCreateJiraIssueButton
			completeKey={completeKey}
			handleCreateIssueItemClicked={handleCreateIssueItemClicked}
			isAIIssueCreationEnabled={isAIIssueCreationEnabled}
			isSelectionMultipleRows={isSelectionMultipleRows}
		/>
	);
};
